import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PortalCashTransactionsLedger,
  cycleSortState,
  downloadBlob,
  type CashTransactionFilterState,
  type CashTransactionListResponse,
  type SortDirection,
  type SortState,
} from '@kioskgaming/ui';
import AppLayout from '../../components/layout/AppLayout';
import { exportSuperAgentCashTransactions, getSuperAgentCashTransactions } from '../../services/api';

export default function CashTransactionsLedgerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<CashTransactionListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortState, setSortState] = useState<SortState>({ sortBy: 'createdAt', sortOrder: 'DESC' });
  const [sortFetchTick, setSortFetchTick] = useState(0);

  // Admin payment pattern: ledger passes sort/sortDir on every fetch.
  // Optional + defaults so compile still works if @kioskgaming/ui types are briefly stale.
  const onFetch = useCallback(
    async ({
      filters,
      page,
      limit,
      sort = 'created',
      sortDir = 'desc',
    }: {
      filters: CashTransactionFilterState;
      page: number;
      limit: number;
      sort?: 'credits' | 'cash' | 'cost' | 'created';
      sortDir?: 'asc' | 'desc';
    }) => {
      setLoading(true);
      try {
        const res = await getSuperAgentCashTransactions({
          page,
          limit,
          status: filters.status || undefined,
          payment_method: filters.paymentMethod || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          search: filters.search.trim() || undefined,
          sort,
          sort_dir: sortDir,
        });
        if (!res.success) throw new Error(res.message || 'Could not load cash transactions');
        setData((res as { data?: CashTransactionListResponse }).data ?? null);
      } catch (e: unknown) {
        setData(null);
        toast.error(e instanceof Error ? e.message : 'Could not load cash transactions');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const handleSort = (key: string) => {
    setSortState((prev) => cycleSortState(prev, key, 'DESC'));
    setSortFetchTick((t) => t + 1);
  };

  const handleExport = useCallback(async ({ filters }: { filters: CashTransactionFilterState }) => {
    setExporting(true);
    try {
      const { blob, filename } = await exportSuperAgentCashTransactions({
        status: filters.status || undefined,
        payment_method: filters.paymentMethod || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        search: filters.search.trim() || undefined,
      });
      downloadBlob(blob, filename);
      toast.success('Cash transactions exported');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, []);

  return (
    <AppLayout>
      <PortalCashTransactionsLedger
        loading={loading}
        data={data}
        onFetch={onFetch}
        onExport={handleExport}
        exporting={exporting}
        showCreatedColumn
        sortable
        sortActiveKey={sortState.sortBy}
        sortDirection={sortState.sortOrder as SortDirection | null}
        onSort={handleSort}
        sortFetchTick={sortFetchTick}
        onViewDetail={(row) =>
          navigate(`/cash-transactions/${row.id}?created_at=${encodeURIComponent(row.createdAt)}`)
        }
        className="border-0 shadow-none rounded-none"
      />
    </AppLayout>
  );
}
